import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

/**
 * A whole week which earned nothing, together with what took it: the `_class` of every absence
 * which covered one of its days (`getWeekCreditBlockers`). Without them a year only states how many
 * weeks it lost, and the person asking why cannot be answered without recalculating.
 */
@Entity()
export class LAKWeekWithoutCredit extends AdornisEntity {
  static override _class = 'LAKWeekWithoutCredit';

  static override resolveID(entity: { start: Maybe<Date> }): string {
    return `${entity.start}`;
  }

  constructor(doc: PartialEntityData<LAKWeekWithoutCredit>) {
    super(doc);
  }

  /**
   * The Monday of the week, which is the day that decides which year and intervall it belongs to.
   */
  @Field(() => Date)
  start: Maybe<Date>;

  /**
   * The `_class` of the absences, so the client renders them through the translation dictionary
   * instead of storing German text in the cache.
   */
  @Field(() => [String], { default: v => v ?? [] })
  absenceClasses: Maybe<string[]>;
}

/**
 * One calendar year of an intervall. The intervalls themselves stay cut along the employment - a
 * contract change, an absence - because that is what the guidelines credit, but the LAK has to be
 * announced on every 31.12., so each intervall additionally carries the years it runs through.
 *
 * A week belongs to the year holding its **Monday**, exactly like it belongs to the intervall
 * holding its Monday, so a week over New Year is neither counted twice nor lost.
 */
@Entity()
export class LAKIntervallYear extends AdornisEntity {
  static override _class = 'LAKIntervallYear';

  static override resolveID(entity: {
    contractID: Maybe<string>;
    supplementID: Maybe<string>;
    adjustmentID: Maybe<string>;
    year: Maybe<number>;
  }): string {
    return `${entity.contractID}_${entity.supplementID}_${entity.adjustmentID}_${entity.year}`;
  }

  constructor(doc: PartialEntityData<LAKIntervallYear>) {
    super(doc);
  }

  @Field(() => ID)
  contractID: Maybe<string>;

  /**
   * Set when the year belongs to a manual Abstimmung. Part of the id, because two Abstimmungen in the same year
   * would otherwise resolve to one entity and be merged in the client cache.
   */
  @Field(() => ID)
  adjustmentID: Maybe<string>;

  @Field(() => ID)
  supplementID: Maybe<string>;

  @Field(() => Int)
  year: Maybe<number>;

  /**
   * The part of the year this intervall covers, so the overview can show the period without
   * recalculating where the year cuts the intervall.
   */
  @Field(() => Date)
  start: Maybe<Date>;

  @Field(() => Date)
  end: Maybe<Date>;

  /**
   * The whole weeks of this year credited up to today.
   */
  @Field(() => Int)
  creditedWeeks: Maybe<number>;

  /**
   * Only set when this year reaches beyond today: the whole weeks credited over the entire year.
   */
  @Field(() => Int)
  projectedCreditedWeeks: Maybe<number>;

  /**
   * The whole weeks of this year which earned nothing, because a day without pay fell into them,
   * each with the absences it was lost to. Together with `creditedWeeks` this explains why a year
   * earns less than its length suggests, and why the weeks are missing.
   */
  @Field(() => [LAKWeekWithoutCredit], { default: v => v ?? [] })
  weeksWithoutCredit: Maybe<LAKWeekWithoutCredit[]>;

  @Field(() => Float)
  workingHours: Maybe<number>;

  /**
   * Only set when this year reaches beyond today: the hours of the entire year, while
   * `workingHours` holds only the part accumulated up to today.
   */
  @Field(() => Float)
  projectedWorkingHours: Maybe<number>;
}

@Entity()
export class LAKIntervall extends AdornisEntity {
  static override _class = 'LAKIntervall';

  static override resolveID(entity: {
    contractID: Maybe<string>;
    supplementID: Maybe<string>;
    adjustmentID: Maybe<string>;
    start: Maybe<Date>;
  }): string {
    // should always be set but on creating the entity it might be empty temporarily
    return `${entity.contractID}_${entity.supplementID}_${entity.adjustmentID}_${entity.start}`;
  }

  constructor(doc: PartialEntityData<LAKIntervall>) {
    super(doc);
  }

  /**
   * Marks the opening balance of the timeline, which carries the hours accumulated before this system
   * calculated the LAK (`MitarbeiterDaten.previousLAKHours`). It belongs to no contract and covers no
   * period, but is part of the chain so every sum over the intervalls starts from it.
   */
  @Field(() => Boolean)
  isPreviousHours: Maybe<boolean>;

  @Field(() => ID)
  contractID: Maybe<string>;

  @Field(() => ID)
  supplementID: Maybe<string>;

  /**
   * Set when the intervall is a manual Abstimmung (`MitarbeiterDaten.lakAdjustments`) rather than part of a
   * contract. It runs in parallel to the contract intervalls and never continues or is continued by one.
   */
  @Field(() => ID)
  adjustmentID: Maybe<string>;

  /**
   * The name of the Abstimmung, so the overview can explain its hours without loading the person.
   */
  @Field(() => String)
  adjustmentName: Maybe<string>;

  /**
   * The Abstimmung books its hours once on `start` instead of per week, so it has no weeks to show.
   */
  @Field(() => Boolean)
  isOneTimeAdjustment: Maybe<boolean>;

  @Field(() => Date)
  start: Maybe<Date>;

  @Field(() => Date)
  end: Maybe<Date>;

  @Field(() => Float)
  workingHoursPerWeek: Maybe<number>;

  /**
   * The LAK earned per calendar week: one hour for a full position, the corresponding share of it for
   * everybody else.
   */
  @Field(() => Float)
  lakHoursPerWeek: Maybe<number>;

  /**
   * The whole weeks credited up to today. Weeks without pay (unpaid leave, illness from the 7th week
   * on) are already taken out, which is why this can be less than the intervall is long.
   */
  @Field(() => Int)
  creditedWeeks: Maybe<number>;

  /**
   * Only set when this interval extends beyond today. The whole weeks credited over the entire
   * intervall, while creditedWeeks holds only those up to today.
   */
  @Field(() => Int)
  projectedCreditedWeeks: Maybe<number>;

  /**
   * Rounded to two digits behind the
   */
  @Field(() => Float)
  workingHoursInIntervall: Maybe<number>;

  /**
   * Only set when this interval extends beyond today (projection up to the 60th birthday).
   * Represents the full projected hours for the entire interval, while workingHoursInIntervall
   * holds only the portion accumulated up to today.
   */
  @Field(() => Float)
  projectedWorkingHoursInIntervall: Maybe<number>;

  /**
   * The whole weeks of this intervall which earned nothing, because a day without pay fell into
   * them, each with the absences it was lost to. Without them `creditedWeeks` cannot be told apart
   * from an intervall which is simply shorter.
   */
  @Field(() => [LAKWeekWithoutCredit], { default: v => v ?? [] })
  weeksWithoutCredit: Maybe<LAKWeekWithoutCredit[]>;

  /**
   * The intervall broken down into the calendar years it runs through, for the yearly statement.
   */
  @Field(() => [LAKIntervallYear], { default: v => v ?? [] })
  years: Maybe<LAKIntervallYear[]>;
}

@Entity()
export class LAKIntervallResult extends MongoEntity {
  static override _class = 'LAKIntervallResult';
  static override _collectionName = 'lak-cache';

  constructor(doc: PartialEntityData<LAKIntervallResult>) {
    super(doc);
  }

  @Field(() => Date, { default: () => new Date() })
  createdAt: Maybe<Date>;

  @Field(() => String)
  userID: Maybe<string>;

  @Field(() => Date)
  retirementDate: Maybe<Date>;

  /**
   * The last day on which LAK is earned - the day before the 60th birthday, or the end of the
   * Schulhalbjahr it falls into for Lehrkräfte, or the day before a Schwerbehinderung if that comes
   * first. Unset when neither is known, in which case nothing can be projected.
   */
  @Field(() => Date)
  creditEndDate: Maybe<Date>;

  /**
   * Whether `creditEndDate` follows the Lehrkräfte rules of the Kultusministerium, which run the
   * credit to the end of the Schulhalbjahr instead of stopping it on the birthday. Only kept so the
   * overview can name the date it shows.
   */
  @Field(() => Boolean)
  creditEndFollowsSchulhalbjahr: Maybe<boolean>;

  /**
   * Whether `creditEndDate` comes from a Schwerbehinderung rather than the 60th birthday. Only kept so the
   * overview can name the date it shows.
   */
  @Field(() => Boolean)
  creditEndBySchwerbehinderung: Maybe<boolean>;

  /**
   * The calendar year the Übertrag closes, and therefore the last year which is not calculated from
   * the contracts. Always set, because an Übertrag without a maintained Stichtag falls back to the
   * year before the calculation may start at all.
   */
  @Field(() => Int)
  previousLAKHoursYear: Maybe<number>;

  /**
   * The first day the contracts earn LAK: the 1st of January after the Übertrag.
   */
  @Field(() => Date)
  creditStartDate: Maybe<Date>;

  @Field(() => [LAKIntervall], { default: v => v ?? [] })
  intervalls: Maybe<LAKIntervall[]>;
}
